import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, MapPin, Phone, Mail } from "lucide-react";
import { BIZ } from "@/data/site";
import { bySide } from "@/data/coverage";
import { INDUSTRIES } from "@/data/industries";
import { useLang } from "@/i18n/LanguageContext";

const NAV = [
  {
    id: "business", label: "Business",
    items: [
      { label: "All business insurance", to: "/business" },
      ...bySide("business").map((c) => ({ label: c.name, to: `/coverage/${c.slug}` })),
      { divider: true, label: "Industries we serve" },
      ...INDUSTRIES.map((i) => ({ label: i.name, to: `/industries/${i.slug}` })),
    ],
  },
  {
    id: "personal", label: "Personal",
    items: [
      { label: "All personal insurance", to: "/personal" },
      ...bySide("personal").map((c) => ({ label: c.name, to: `/coverage/${c.slug}` })),
    ],
  },
  {
    id: "specialty", label: "Specialty",
    items: [
      { label: "All specialty lines", to: "/specialty" },
      ...bySide("specialty").map((c) => ({ label: c.name, to: `/coverage/${c.slug}` })),
    ],
  },
  {
    id: "customer", label: "Customer login",
    items: [
      { label: "Log in", to: "/login" },
      { label: "Pay my premium", to: "/pay" },
      { label: "Report a claim", to: "/claims" },
      { label: "Request a certificate", to: "/certificate" },
      { label: "Free policy review", to: "/policy-review" },
    ],
  },
];

function Dropdown({ group, open, setOpen }) {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [open, setOpen]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`nav-${group.id}-trigger`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(open ? null : group.id)}
        className={`flex items-center gap-1 px-3 py-2 text-[15px] font-medium transition-colors ${open ? "text-[var(--ink)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
      >
        {t(group.label)}
        <ChevronDown size={14} strokeWidth={1.75} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`nav-${group.id}-menu`}
          className="absolute left-0 top-full z-50 mt-1 w-72 border border-[var(--line)] bg-white py-2 shadow-[0_10px_30px_rgba(6,36,77,0.10)]"
        >
          {group.items.map((item) =>
            item.divider ? (
              <div key={`divider-${item.label}`} className="mt-2 border-t border-[var(--line)] px-4 pt-2 pb-1 text-xs font-semibold text-[var(--ink-soft)]">
                {t(item.label)}
              </div>
            ) : (
              <button
                key={item.to}
                role="menuitem"
                onClick={() => { setOpen(null); navigate(item.to); }}
                className="block w-full px-4 py-2 text-left text-[14px] text-[var(--ink-soft)] hover:bg-[var(--tint)] hover:text-[var(--ink)]"
              >
                {t(item.label)}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

function MobileMenu({ group, setGroup }) {
  const { t } = useLang();
  return (
    <div className="max-h-[calc(100vh-104px)] overflow-y-auto border-b border-[var(--line)] bg-white lg:hidden" data-testid="mobile-menu">
      <div className="wrap py-3">
        {NAV.map((g) => (
          <div key={g.id} className="border-b border-[var(--line)]">
            <button
              type="button"
              aria-expanded={group === g.id}
              onClick={() => setGroup(group === g.id ? null : g.id)}
              className="flex w-full items-center justify-between py-3.5 text-left text-[16px] font-medium text-[var(--ink)]"
              data-testid={`mobile-nav-${g.id}`}
            >
              {t(g.label)}
              <ChevronDown size={16} strokeWidth={1.75} className={`transition-transform ${group === g.id ? "rotate-180" : ""}`} />
            </button>
            {group === g.id && (
              <div className="pb-3">
                {g.items.map((item) =>
                  item.divider ? (
                    <div key={`divider-${item.label}`} className="px-3 pt-3 pb-1 text-xs font-semibold text-[var(--ink-soft)]">{t(item.label)}</div>
                  ) : (
                    <Link key={item.to} to={item.to} className="block px-3 py-2 text-[15px] text-[var(--ink-soft)]">
                      {t(item.label)}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>
        ))}
        <Link to="/why-us" className="block py-3.5 text-[16px] font-medium text-[var(--ink)]">{t("Why us")}</Link>
        <Link to="/quote" className="btn btn-green mt-2 mb-4 w-full">{t("Get my quote")}</Link>
      </div>
    </div>
  );
}

export const SiteHeader = () => {
  const [openId, setOpenId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const { pathname } = useLocation();
  const { lang, setLang, t } = useLang();

  useEffect(() => { setOpenId(null); setMobileOpen(false); }, [pathname]);

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-[var(--ink-deep)] text-[13px] text-white/80">
        <div className="wrap flex h-9 items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-5">
            <span className="hidden items-center gap-1.5 truncate lg:flex">
              <MapPin size={13} strokeWidth={1.75} /> {BIZ.addressShort}
            </span>
            <span className="hidden sm:inline">{t("Licensed in Texas")}</span>
          </div>
          <div className="flex items-center gap-5">
            <button
              type="button"
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              aria-label={lang === "en" ? "Cambiar a español" : "Switch to English"}
              className="border border-white/30 px-2 py-0.5 text-[12px] font-semibold text-white/85 transition-colors hover:border-white hover:text-white"
            >
              {lang === "en" ? "Español" : "English"}
            </button>
            <a href={BIZ.phoneHref} className="flex items-center gap-1.5 hover:text-white" data-testid="utility-phone-link">
              <Phone size={13} strokeWidth={1.75} /> {BIZ.phone}
            </a>
            <a href={BIZ.emailHref} className="hidden items-center gap-1.5 hover:text-white md:flex" data-testid="utility-email-link">
              <Mail size={13} strokeWidth={1.75} /> {BIZ.email}
            </a>
          </div>
        </div>
      </div>

      <div className="border-b border-[var(--line)] bg-white">
        <div className="wrap flex h-[68px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src="/artha-logo.png" alt="Artha Insurance LLC — home" width="120" height="40" className="h-10 w-auto" />
            <span className="font-display text-[21px] leading-none text-[var(--ink)]">
              Artha Insurance
              <span className="mt-0.5 block font-sans text-[10.5px] font-medium tracking-wide text-[var(--ink-soft)]">
                {t("Independent agency · Frisco, Texas")}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {NAV.map((g) => (
              <Dropdown key={g.id} group={g} open={openId === g.id} setOpen={setOpenId} />
            ))}
            <Link
              to="/why-us"
              data-testid="nav-why-us-link"
              className="px-3 py-2 text-[15px] font-medium text-[var(--ink-soft)] hover:text-[var(--ink)]"
            >
              {t("Why us")}
            </Link>
            <Link to="/quote" className="btn btn-green ml-3 !px-5 !py-3" data-testid="header-quote-button">
              {t("Get my quote")}
            </Link>
          </nav>

          <button
            type="button"
            className="p-2 text-[var(--ink)] lg:hidden"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} strokeWidth={1.75} /> : <Menu size={24} strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileMenu group={mobileGroup} setGroup={setMobileGroup} />}
    </header>
  );
};
